<template>
    <picture>
        <template
            v-if="pictureSrcSetAsWebp != null && pictureSrcSetAsWebp != ''"
        >
            <source :srcset="pictureSrcSetAsWebp" type="image/webp" />
        </template>
        <template
            v-else-if="pictureUrlAsWebp != null && pictureUrlAsWebp != ''"
        >
            <source :srcset="pictureUrlAsWebp" type="image/webp" />
        </template>
        <img
            :class="{ 'fill-width': fillWidth }"
            :srcset="pictureSrcSet ?? undefined"
            :alt="
                item != null
                    ? item.pictureAltText ??
                      item.productName ??
                      item.productName
                    : undefined
            "
            :src="pictureUrl"
            :loading="lazy ? 'lazy' : undefined"
            :width="pictureWidth"
            :height="pictureHeight"
            :sizes="sizesSpec"
        />
    </picture>
</template>

<style scoped>
img.fill-width {
    width: 100%;
    object-fit: contain;
    aspect-ratio: 4/3;
    height: auto;
}
</style>

<script setup lang="ts">
import {
    type PropType,
    computed,
    onMounted,
    getCurrentInstance,
    ref,
} from "vue";
import { type IProductPictureInfo } from "../../list-item";
import { spacerUrl } from "../../../../shared/empty-pixel";
import { getWindowInnerWidth } from "../../../../../../utilities/browser-size";
import fastdom from "fastdom";

const props = defineProps({
    item: {
        type: Object as PropType<
            IProductPictureInfo & {
                pictureAltText?: string;
                productName?: string;
                name?: string;
            }
        >,
    },
    fixedWidth: {
        type: Number,
    },
    fillWidth: Boolean,
    fallbackWidth: [Number, String],
    fallbackHeight: [Number, String],
    scaleOfScreen: {
        type: Number,
        default: 1,
    },
    lazy: Boolean,
    providedImageWidth: Number,
});

const emit = defineEmits(["gotImageWidth"]);

const width = ref(-1);

const instance = getCurrentInstance();

onMounted(() => {
    if (instance == null) throw new Error("missing instance");

    if (props.providedImageWidth != null) {
        width.value = props.providedImageWidth;
    } else {
        fastdom.measure(() => {
            const rect = instance.proxy?.$el.getBoundingClientRect();
            width.value = rect.width;
            emit("gotImageWidth", rect.width);
        });
    }
});

const innerWidth = getWindowInnerWidth();

const actualScale = computed(() => {
    if (width.value === -1) return props.scaleOfScreen;
    return width.value / innerWidth.value;
});

const pictureUrlAsWebp = computed(() => {
    if (props.item == null) {
        return undefined;
    }
    return patchUrl(props.item.pictureUrlAsWebp ?? "", props.fixedWidth);
});

const pictureSrcSetAsWebp = computed(() => {
    return props.item?.pictureSrcSetAsWebp;
});
const pictureSrcSet = computed(() => {
    return props.item?.pictureSrcSet;
});

const fallbackWidthNumber = computed(() =>
    typeof props.fallbackWidth === "string"
        ? parseInt(props.fallbackWidth)
        : props.fallbackWidth
);
const fallbackHeightNumber = computed(() =>
    typeof props.fallbackHeight === "string"
        ? parseInt(props.fallbackHeight)
        : props.fallbackHeight
);

const pictureUrl = computed(() => {
    if (props.item == null) {
        return spacerUrl;
    }

    if (props.item.pictureUrl == null) {
        return spacerUrl;
    }

    return patchUrl(props.item.pictureUrl, props.fixedWidth);
});

const sizesSpec = computed(() => {
    if (actualScale.value != null) {
        return `${actualScale.value * 100}vw`;
    }
    return undefined;
});
const pictureWidth = computed(() => {
    if (props.fixedWidth != null) {
        return props.fixedWidth;
    }
    if (props.item == null) {
        return fallbackWidthNumber.value;
    }
    return props.item.pictureWidth ?? undefined;
});
const pictureHeight = computed(() => {
    if (props.item == null) {
        return fallbackHeightNumber.value;
    }
    if (props.fixedWidth != null) {
        const ratio =
            (props.item.pictureWidth ?? 1) / (props.item.pictureHeight ?? 1);
        return props.fixedWidth / ratio;
    }
    return props.item.pictureHeight ?? undefined;
});

function patchUrl(url: string, fixedWidth: number | undefined) {
    if (fixedWidth != null) {
        const urlObject = new URL(url);
        urlObject.searchParams.set("width", fixedWidth.toString());
        url = urlObject.toString();
    }
    return url;
}
</script>
